/**
 * ComparisonChart Component
 * Monthly bar chart comparing YTD vs Last Year revenue
 * Shows net revenue bars side-by-side per month
 */

import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from "recharts";
import {
    formatCurrencyValue,
    getCurrencySymbolForDashboard,
    calculateGrowth
} from "../../utils/dashboardUtils";

/**
 * Tooltip row component for consistent formatting
 */
function TooltipRow({ label, value, currency = "USD", highlight = false }) {
    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 2,
                py: 0.25
            }}
        >
            <Typography
                variant="body2"
                sx={{
                    color: highlight ? "#333" : "#666",
                    fontWeight: highlight ? 600 : 400
                }}
            >
                {label}
            </Typography>
            <Typography
                variant="body2"
                sx={{
                    fontWeight: highlight ? 600 : 400,
                    color: "#333"
                }}
            >
                {formatCurrencyValue(value || 0, currency)}
            </Typography>
        </Box>
    );
}

/**
 * Custom tooltip for the bar chart - shows Gross revenue, Net revenue, and percentage change for current year
 */
function CustomTooltip({
    active,
    payload,
    label,
    currentYear,
    lastYear,
    currency = "USD"
}) {
    if (!active || !payload || !payload.length) {
        return null;
    }

    const data = payload[0]?.payload;
    if (!data) return null;

    // Calculate gross and net for each year
    // Gross = Booked + Additional
    // Net = Gross - Refunds
    const ytdGross = (data.ytdBooked || 0) + (data.ytdAdditional || 0);
    const ytdNet = data.ytdNet || 0;

    const lastYearGross =
        (data.lastYearBooked || 0) + (data.lastYearAdditional || 0);
    const lastYearNet = data.lastYearNet || 0;

    // Calculate percentage change for current year vs last year
    const growth = calculateGrowth(ytdNet, lastYearNet);

    return (
        <Box
            sx={{
                backgroundColor: "white",
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                p: 1.5,
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                minWidth: 180
            }}
        >
            <Typography
                variant="subtitle2"
                sx={{
                    fontWeight: 600,
                    mb: 1.5,
                    borderBottom: "1px solid #eee",
                    pb: 0.5
                }}
            >
                {label}
            </Typography>

            {/* Current Year */}
            <Typography
                variant="caption"
                sx={{
                    fontWeight: 600,
                    color: "#333",
                    display: "block",
                    mb: 0.5
                }}
            >
                {currentYear}
            </Typography>
            <TooltipRow label="Gross" value={ytdGross} currency={currency} />
            <TooltipRow
                label="Net"
                value={ytdNet}
                currency={currency}
                highlight
            />
            {/* Show percentage change for current year */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 2,
                    py: 0.25,
                    mt: 0.5
                }}
            >
                <Typography
                    variant="body2"
                    sx={{ color: "#666", fontSize: "0.75rem" }}
                >
                    vs {lastYear}
                </Typography>
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        color: growth.isZero
                            ? "#666"
                            : growth.isPositive
                              ? "#2e7d32"
                              : "#c62828"
                    }}
                >
                    {growth.isZero
                        ? "0%"
                        : `${growth.isPositive ? "+" : "-"}${growth.percentage.toFixed(1)}%`}
                </Typography>
            </Box>

            {/* Last Year */}
            <Typography
                variant="caption"
                sx={{
                    fontWeight: 600,
                    color: "#333",
                    display: "block",
                    mt: 1.5,
                    mb: 0.5
                }}
            >
                {lastYear}
            </Typography>
            <TooltipRow
                label="Gross"
                value={lastYearGross}
                currency={currency}
            />
            <TooltipRow
                label="Net"
                value={lastYearNet}
                currency={currency}
                highlight
            />
        </Box>
    );
}

/**
 * @param {Object} props
 * @param {Array} props.data - Chart data array
 * @param {number} props.currentYear - Current year
 * @param {number} props.lastYear - Last year
 * @param {boolean} props.showGrowthIndicator - Whether to show growth indicators
 * @param {number} props.height - Chart height in pixels
 * @param {string} props.currency - Currency code (USD, GBP, etc.)
 */
function ComparisonChart({
    data,
    currentYear,
    lastYear,
    showGrowthIndicator = true,
    height = 400,
    currency = "USD"
}) {
    const theme = useTheme();

    // Format Y-axis values with proper currency symbol
    const currencySymbol = getCurrencySymbolForDashboard(currency);
    const formatYAxis = (value) => {
        if (value >= 1000000) {
            return `${currencySymbol}${(value / 1000000).toFixed(1)}M`;
        }
        if (value >= 1000) {
            return `${currencySymbol}${(value / 1000).toFixed(0)}K`;
        }
        return `${currencySymbol}${value}`;
    };

    // Colors for the bars
    const currentYearColor = theme.palette.primary.main;
    const lastYearColor = "#9e9e9e"; // Grey for last year

    return (
        <Box>
            {/* Chart Header */}
            <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Net revenue by month
                </Typography>
            </Box>

            {/* Chart */}
            <ResponsiveContainer width="100%" height={height}>
                <BarChart
                    data={data}
                    margin={{
                        top: 20,
                        right: 30,
                        left: 20,
                        bottom: 5
                    }}
                    barGap={2}
                    barCategoryGap="20%"
                >
                    <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={theme.palette.divider}
                    />
                    <XAxis
                        dataKey="month"
                        tick={{ fontSize: 12 }}
                        tickLine={false}
                        axisLine={{ stroke: theme.palette.divider }}
                    />
                    <YAxis
                        tickFormatter={formatYAxis}
                        tick={{ fontSize: 12 }}
                        tickLine={false}
                        axisLine={{ stroke: theme.palette.divider }}
                    />
                    <Tooltip
                        content={
                            <CustomTooltip
                                currentYear={currentYear}
                                lastYear={lastYear}
                                currency={currency}
                            />
                        }
                    />
                    <Legend
                        payload={[
                            {
                                value: `${currentYear}`,
                                type: "square",
                                color: currentYearColor
                            },
                            {
                                value: `${lastYear}`,
                                type: "square",
                                color: lastYearColor
                            }
                        ]}
                        wrapperStyle={{
                            paddingTop: "20px"
                        }}
                        formatter={(value) => (
                            <span style={{ color: theme.palette.text.primary }}>
                                {value}
                            </span>
                        )}
                    />
                    {/* Current Year Net Revenue Bar */}
                    <Bar
                        dataKey={currentYear}
                        fill={currentYearColor}
                        radius={[4, 4, 0, 0]}
                        maxBarSize={40}
                        name={`${currentYear}`}
                    />
                    {/* Last Year Net Revenue Bar */}
                    <Bar
                        dataKey={lastYear}
                        fill={lastYearColor}
                        radius={[4, 4, 0, 0]}
                        maxBarSize={40}
                        name={`${lastYear}`}
                    />
                </BarChart>
            </ResponsiveContainer>
        </Box>
    );
}

export default ComparisonChart;
